import { PaymentMethod } from '@wix/cashier-common/dist/src/enums/payments/PaymentMethod';
import { ServerTransactionStatus } from '@wix/cashier-common/dist/src/enums/transactions/ServerTransactionStatus';
import { FedopsLogger } from '@wix/fedops-logger';
import qs from 'qs';
import React, { useCallback } from 'react';
import { InteractionName } from '../../../types/InteractionName';
import { t } from '../../../utils/i18n';
import { parseURL } from '../../../utils/parseURL';
import { FramedModal } from '../FramedModal';
import { FramedModalProps } from '../FramedModal/controller';
import { finishMessageType, payModalName } from './constants';
import { PayModalResponse } from '../../../sdk/types/PayModal';

export interface PayModalProps {
  url: string;
  paymentMethod: PaymentMethod;
  publicBaseUrl: string;
  locale: string;
  userId: string;
  widgetVersion?: string;
  width?: number | string;
  height?: number | string;
  fedopsLogger: FedopsLogger<InteractionName>;

  onManualClose(): void;
  onResponse(response: PayModalResponse): void;
}

export const PayModalProvider: React.FC<
  PayModalProps & {
    children(
      props: FramedModalProps
    ): React.ReactComponentElement<typeof FramedModal>;
  }
> = (props) => {
  const handleReadyTimeout = useCallback(() => {
    props.onResponse({
      status: ServerTransactionStatus.Timeout,
      failureCode: '5000',
    });
  }, [props.onResponse]);

  const handleFrameMount = useCallback(() => {
    props.fedopsLogger.interactionEnded(InteractionName.PAY_MODAL_ALE);
  }, []);

  const handleMessageFromProxyPage = useCallback(
    (data: { type: string; payload: string }) => {
      if (data.type === finishMessageType) {
        const response = qs.parse(data.payload) as unknown as PayModalResponse;

        props.onResponse(response);
      }
    },
    [props.onResponse]
  );

  return props.children({
    modalName: payModalName,
    url: props.url,
    callbackPageOrigin: parseURL(props.publicBaseUrl)?.origin,
    locale: props.locale,
    width: props.width,
    height: props.height,
    publicBaseUrl: props.publicBaseUrl,
    userId: props.userId,
    widgetVersion: props.widgetVersion,
    fedopsLogger: props.fedopsLogger,
    title: t(
      props.paymentMethod === PaymentMethod.CreditCard
        ? 'paymodal.title'
        : 'paymodal.apm.title'
    ),
    onFrameMount: handleFrameMount,
    onClose: props.onManualClose,
    onReadyTimeout: handleReadyTimeout,
    onTransitiveMessageFromFrame: handleMessageFromProxyPage,
  });
};
PayModalProvider.displayName = 'PayModalProvider';

export const PayModal: React.FC<PayModalProps> = (props) => (
  <PayModalProvider {...props}>
    {(renderProps) => <FramedModal {...renderProps} />}
  </PayModalProvider>
);
PayModal.displayName = 'PayModal';
